<script setup>
import { computed } from 'vue';
import { useReportMetrics } from 'dashboard/composables/useReportMetrics';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { STATUS } from 'dashboard/store/constants';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  metric: {
    type: Object,
    default: () => ({}),
  },
  accountSummaryKey: {
    type: String,
    default: 'getAccountSummary',
  },
  summaryFetchingKey: {
    type: String,
    default: 'getAccountSummaryFetchingStatus',
  },
});

const { t } = useI18n();

const { calculateTrend, displayMetric, isAverageMetricType, fetchingStatus } =
  useReportMetrics(props.accountSummaryKey, props.summaryFetchingKey);

// Ícone + cor por métrica, mesmo vocabulário de cores do
// components-next/Reports/MetricCard.vue (usado na Visão Geral). Chaves fora
// do mapa (ex.: bot_resolutions_count/bot_handoffs_count do BotReports.vue,
// que também reaproveita este componente via ReportContainer.vue) caem no
// fallback em vez de ficar sem ícone.
const METRIC_ICON_MAP = {
  conversations_count: { icon: 'i-lucide-message-circle', color: 'blue' },
  incoming_messages_count: { icon: 'i-lucide-inbox', color: 'teal' },
  outgoing_messages_count: { icon: 'i-lucide-send', color: 'green' },
  avg_first_response_time: { icon: 'i-lucide-timer', color: 'amber' },
  avg_resolution_time: { icon: 'i-lucide-check-circle', color: 'ruby' },
  resolutions_count: { icon: 'i-lucide-list-checks', color: 'slate' },
  reply_time: { icon: 'i-lucide-hourglass', color: 'blue' },
};
const DEFAULT_ICON = { icon: 'i-lucide-bar-chart-3', color: 'slate' };

const colorClasses = {
  slate: 'bg-n-alpha-2 text-n-slate-11',
  blue: 'bg-n-blue-3 text-n-blue-11',
  teal: 'bg-n-teal-3 text-n-teal-11',
  green: 'bg-n-teal-3 text-n-teal-11',
  amber: 'bg-n-amber-3 text-n-amber-11',
  yellow: 'bg-n-amber-3 text-n-amber-11',
  ruby: 'bg-n-ruby-3 text-n-ruby-11',
  red: 'bg-n-ruby-3 text-n-ruby-11',
};

const iconMeta = computed(
  () => METRIC_ICON_MAP[props.metric.KEY] || DEFAULT_ICON
);
const iconColorClass = computed(
  () => colorClasses[iconMeta.value.color] || colorClasses.slate
);

// `trendPositive` é o julgamento SEMÂNTICO (bom/ruim, decide a cor);
// `metric.trend` continua sendo o sinal MATEMÁTICO do número (decide a seta).
// Pra métricas de tempo (isAverageMetricType) subir é ruim, então os dois se
// invertem em relação às métricas de contagem.
const isTrendPositive = computed(() => {
  const { trend, KEY } = props.metric;
  if (isAverageMetricType(KEY)) return trend <= 0;
  return trend >= 0;
});
</script>

<template>
  <div
    class="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4"
  >
    <div
      class="flex size-8 shrink-0 items-center justify-center rounded-lg sm:size-10 sm:rounded-xl"
      :class="iconColorClass"
    >
      <Icon :icon="iconMeta.icon" class="size-4 sm:size-5" />
    </div>
    <div class="min-w-0 flex-1 text-n-slate-11">
      <span class="text-sm">
        {{ metric.NAME }}
      </span>
      <div class="flex items-end text-n-slate-12">
        <div v-if="fetchingStatus === STATUS.FETCHING">
          <Spinner />
        </div>
        <div
          v-else-if="fetchingStatus === STATUS.FAILED"
          class="text-n-ruby-10 text-sm"
        >
          {{ t('REPORT.SUMMARY_FETCHING_FAILED') }}
        </div>
        <div
          v-else-if="fetchingStatus === STATUS.FINISHED"
          class="text-2xl font-semibold tabular-nums"
        >
          {{ displayMetric(metric.KEY) }}
        </div>
        <div
          v-if="metric.trend && fetchingStatus === STATUS.FINISHED"
          class="text-xs font-medium ml-3 flex items-center gap-1 mb-1"
          :class="isTrendPositive ? 'text-n-teal-10' : 'text-n-ruby-9'"
        >
          <Icon
            :icon="
              metric.trend < 0
                ? 'i-lucide-trending-down'
                : 'i-lucide-trending-up'
            "
            class="size-3"
          />
          {{ calculateTrend(metric.KEY) }}%
        </div>
      </div>
    </div>
  </div>
</template>
